import React, { useState } from 'react';
import { Alert, Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useAuth } from '../../../common/hooks/useAuth';
import { useSzafir } from '../../../common/hooks/useSzafir';
import { SigningInProgressModal } from '../Szafir/SigningInProgressModal';
import { signP1Document } from '../Szafir/sendJudgmentToP1';
import { SimP1StatusEnum } from '../../../common/spdCore/validation/enums';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { Role } from '../../../common/spdCore/authorization/roles';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';

export const JudgmentBinLink = (props: {
  judgmentId: string;
  judgmentNumber?: string | null;
  statusP1?: SimP1StatusEnum | null;
  cancelStatusP1?: SimP1StatusEnum | null;
  setRefresh?: () => void;
}) => {
  const spd = useSpdCore();
  const auth = useAuth();
  const szafir = useSzafir();
  const [signing, setSigning] = useState(false);
  const cancelsInP1 =
    props.statusP1 === SimP1StatusEnum.AcceptedInP1 ||
    (props.cancelStatusP1 !== null && props.cancelStatusP1 !== undefined);
  const isAdmin = auth.check([Role.Admin, Role.SuperAdmin]);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const signCancellationIfAllowed = async (documentId: string) => {
    if (!auth.canSign) {
      return;
    }
    setSigning(true);
    try {
      await signP1Document(auth, szafir, spd, documentId);
    } catch {
      // Stays waiting for a signature.
    } finally {
      setSigning(false);
    }
  };

  const handleClick = async (reason?: string) => {
    try {
      const { cancellationDocumentId } = await spd.judgments.changeToInactive(
        props.judgmentId,
        cancelsInP1 ? reason?.trim() : undefined,
      );
      if (cancellationDocumentId) {
        await signCancellationIfAllowed(cancellationDocumentId);
      }
      if (props.setRefresh) {
        props.setRefresh();
      }
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setSaveErrors(errors);
      setShowErrorModal(true);
    }
  };

  if (cancelsInP1 && !isAdmin) {
    return null;
  }

  if (
    props.statusP1 === SimP1StatusEnum.WaitingForP1 ||
    props.cancelStatusP1 === SimP1StatusEnum.WaitingForP1 ||
    props.cancelStatusP1 === SimP1StatusEnum.ToSign
  ) {
    return null;
  }

  if (signing) {
    return <SigningInProgressModal />;
  }

  return (
    <>
      <OverlayTrigger
        overlay={
          <Tooltip style={{ position: 'fixed' }}>
            {cancelsInP1
              ? 'Anulowanie orzeczenia w SIM'
              : 'Usunięcie orzeczenia'}
          </Tooltip>
        }
      >
        <ConfirmationButton
          className="ms-1"
          confirmation={
            cancelsInP1
              ? `Orzeczenie ${props.judgmentNumber} zostało przyjęte w SIM, więc zostanie anulowane w SIM. ` +
                (auth.canSign
                  ? 'Anulowanie zostanie teraz podpisane i wysłane do SIM.'
                  : 'Anulowanie trafi do podpisu lekarza (Dokumenty do podpisu).') +
                ' Orzeczenie zostanie usunięte po przyjęciu anulowania w SIM. Kontynuować?'
              : `Czy na pewno usunąć orzeczenie ${props.judgmentNumber}?`
          }
          variant="btn-info"
          modalSize="xl"
          askForAReason={cancelsInP1}
          reasonNotEmpty
          dynamicInput
          focusInput
          reasonText="Powód anulowania"
          errorLabel="Proszę podać powód anulowania orzeczenia."
          onOK={handleClick}
        >
          <Icon.Trash />
        </ConfirmationButton>
      </OverlayTrigger>
      <Modal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        centered
      >
        <Modal.Body>
          <Alert variant="danger" className="m-0">
            {saveErrors.map((e, i) => (
              <div key={i}>{e}</div>
            ))}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={() => setShowErrorModal(false)}
          >
            Zamknij
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
